/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Add, Download, Upload, TrashCan } from '@carbon/react/icons';
import {
  Button,
  DataTable,
  Pagination,
  Search,
  OverflowMenu,
  OverflowMenuItem
} from 'carbon-components-react';
import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import empty_phone_numbers_table from '@ibm-watson/shared/assets/images/empty_phone_numbers_table.svg';
import { saveAs } from '@ibm-watson/shared/modules/actions/util/download';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { ChannelContentProps } from '../../../util/types';
import { sharedMessages } from '../../messages';
import { MODAL_STATUS } from '../constants';
import { FIELDS } from '../schema';
import { PAGE_SIZES, ASC, DESC, NONE } from './constants';
import { getSortedData } from './helpers';
import { PhoneNumbers as messages, PhoneImportModalMessages } from './messages';
import { PhoneAddModal } from './PhoneAddModal';
import { PhoneImportModal } from './PhoneImportModal';
import './PhoneImportModal.scss';

const {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableSelectAll,
  TableSelectRow,
  TableToolbar,
  TableToolbarContent
} = DataTable;

export const PhoneNumbers = ({ readOnly }: ChannelContentProps) => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { control, handleSubmit, watch } = formMethods;
  const { getFieldValue, updateFieldValue } = useFormHelpers(formMethods);

  const { fields, append, remove } = useFieldArray({
    control,
    name: FIELDS.PHONE_NUMBERS,
    keyName: 'other'
  });

  const watchedFields = watch(FIELDS.PHONE_NUMBERS);

  // Previous state variables & updates
  const [selectedData, setSelectedData] = useState<string[]>([]);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [openImportModal, setOpenImportModal] = useState<boolean>(false);
  const [addModalStatus, setAddModalStatus] = useState<string>(
    MODAL_STATUS.CLOSED
  );
  const [newNumAppended, setNewNumAppended] = useState<boolean>(false);
  const [importing, setImporting] = useState<boolean>(false);
  const [filterTerm, setFilterTerm] = useState<string>('');
  const [sortStateDirection, setSortStateDirection] = useState<string>(NONE);
  const [sortStateLastSort, setSortStateLastSort] = useState<string>('noSort');
  const [displayRows, setDisplayRows] = useState<any>(watchedFields || []);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [changingPrimary, setChangingPrimary] = useState<boolean>(false);

  // Previous props
  const assistantId = '';

  const headers = [
    { key: 'phone_number', header: t(messages.phoneNumber) },
    { key: 'name', header: t(messages.description) },
    { key: 'primary', header: t(messages.status) },
    { key: 'action', header: '' }
  ];

  const toggleSortOrder = (e, headerTarget) => {
    // headerTarget one of [phone_number, description, primary]
    //  note that 'primary' column also displays error, displayed title "Status"
    let nextSortDirection = '';
    let lastSort = '';
    // direction changes only when the same header is clicked a 2nd or
    //  3rd+ time, cycling thru asc -> desc -> none
    // any time the target header changes, dir is asc for the new target
    if (headerTarget === sortStateLastSort) {
      if (sortStateDirection === ASC) {
        nextSortDirection = DESC;
      } else {
        if (sortStateDirection === DESC) {
          nextSortDirection = NONE;
        } else {
          nextSortDirection = ASC;
        }
      }
    } else {
      // changed target header
      nextSortDirection = ASC;
      lastSort = headerTarget;
    }
    const sortedFilter = getSortedData(fields, headerTarget, nextSortDirection);
    setSortStateDirection(nextSortDirection);
    setSortStateLastSort(lastSort);
    setCurrentPage(1);
    setDisplayRows(sortedFilter);
  };

  // Handler functions
  const handleExport = () => {
    let data = '';
    watchedFields.forEach(entry => {
      let description = entry.name ? entry.name : '';
      description = description.replaceAll('"', '""'); //escape " to ""
      if (description.includes(',') || description.includes('"')) {
        // if any commas or quotes, wrap whole phrase in " "
        description = '"' + description + '"';
      }
      data = data.concat(entry.phone_number + ',' + description + '\n');
    });
    const file = new Blob([data], { type: 'text/csv; charset=UTF-8' });
    const fileprefix = assistantId ? assistantId + '_' : '';
    const filename = fileprefix + 'phoneNumbers.csv';
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(file, filename);
    } else {
      saveAs(URL.createObjectURL(file), filename);
    }
  };

  const handleImportSave = importedArray => {
    // append the imported array to the existing phoneNumbers
    // if there are no existing phone numbers, sets first imported number to be primary number
    if (
      watchedFields &&
      watchedFields.length === 0 &&
      importedArray.length !== 0
    ) {
      importedArray[0].primary = true;
    }
    append(importedArray);
    setFilterTerm('');
    setPageSize(PAGE_SIZES[0]);
    setCurrentPage(1);
    setOpenImportModal(false);
    setAddModalStatus(MODAL_STATUS.CLOSED);
    setSelectedData([]);
  };

  // Sets a new phone number as the primary phone number in use
  const handleSetPrimary = rowId => {
    const oldPrimaryIndex = watchedFields?.findIndex(entry => entry.primary);
    updateFieldValue(
      `${FIELDS.PHONE_NUMBERS}[${oldPrimaryIndex}].primary`,
      false
    );
    const newPrimaryIndex = watchedFields?.findIndex(
      entry => entry.id === rowId
    );
    updateFieldValue(
      `${FIELDS.PHONE_NUMBERS}[${newPrimaryIndex}].primary`,
      true
    );
    setSortStateDirection(NONE);
    setSortStateLastSort('noSort');
    setChangingPrimary(true);
  };

  const filterRows = (filterTerm, origArray) => {
    const newFilterData = filterTerm
      ? origArray.filter(
          phoneObj =>
            phoneObj.phone_number
              .toLowerCase()
              .includes(filterTerm.toLowerCase()) ||
            phoneObj.name.toLowerCase().includes(filterTerm.toLowerCase())
        )
      : origArray;
    return newFilterData;
  };

  // NOTE any deletion will recalculate IDs so must clear selection
  const deleteRows = deleteIndices => {
    let index, item, updatedItem;
    let noPrimary = true;
    const deleteArray = [];
    for (let i = 0; i < deleteIndices.length; i++) {
      index = Number(deleteIndices[i]);
      deleteArray.push(watchedFields[index]);
    }
    remove(deleteArray);
    const newFields = watchedFields.filter(
      field => !deleteArray.includes(field)
    );

    // Updating ids for existing phone numbers
    for (let i = 0; i < newFields.length; i++) {
      item = newFields[i];
      if (item.id !== i) {
        updatedItem = {
          id: i.toString(),
          name: item.name,
          primary: item.primary,
          phone_number: item.phone_number
        };
        if (item.primary) {
          noPrimary = false;
        }
        newFields[i] = updatedItem;
      }
    }
    updateFieldValue(FIELDS.PHONE_NUMBERS, newFields);
    // Setting primary phone to first phone number if primary phone was deleted
    if (noPrimary && newFields.length !== 0) {
      updateFieldValue(`${FIELDS.PHONE_NUMBERS}[0].primary`, true);
    }
    setDisplayRows(watchedFields);
    setDeleting(true);
    setSelectedData([]);
  };

  const handleDeleteSingleRow = rowId => {
    deleteRows([rowId]);
  };

  const handleDeleteSelection = _event => {
    deleteRows(selectedData);
  };

  const handleRowSelect = row => {
    const isThere = selectedData.some(i => i === row.id);
    const revisedSelectedData = isThere
      ? selectedData.filter(i => i !== row.id)
      : selectedData.concat(row.id);
    setSelectedData(revisedSelectedData);
  };

  const toggleSelectAll = () => {
    // note designed to only select currently visible rows
    const selectedDataLength = selectedData.length;
    if (selectedDataLength >= 0 && selectedDataLength < displayRows.length) {
      // not all already selected, so select all
      const ids = [];
      displayRows.forEach(phoneObj => {
        ids.push(phoneObj.id);
      });
      setSelectedData(ids);
    } else {
      // already all selected, so deselect all
      setSelectedData([]);
    }
  };

  // When Tanya opens add phone number modal, add a new number to array
  // If Tanya clicks close in add phone number modal, delete last element in phone numbers array
  useEffect(() => {
    if (addModalStatus === MODAL_STATUS.OPEN) {
      const primary =
        watchedFields === undefined || watchedFields?.length === 0;
      const newNum = {
        id: watchedFields?.length.toString() || '0',
        phone_number: '',
        name: '',
        primary: primary
      };
      append(newNum);
      setNewNumAppended(true);
    } else if (addModalStatus === MODAL_STATUS.CANCELLED) {
      const lastIndex =
        watchedFields && watchedFields.length !== 0
          ? watchedFields.length - 1
          : 0;
      remove([lastIndex]);
      setNewNumAppended(false);
    } else if (addModalStatus === MODAL_STATUS.CLOSED) {
      setNewNumAppended(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addModalStatus]);

  // Updates display rows when changing pages, searching, deleting, or setting primary
  useEffect(() => {
    let modArray = _.cloneDeep(getFieldValue(FIELDS.PHONE_NUMBERS));
    if (addModalStatus === MODAL_STATUS.OPEN) {
      modArray = modArray.slice(0, -1);
    }
    const newFilterData = filterRows(filterTerm, modArray);
    let startIndex = (currentPage - 1) * pageSize;
    let endIndex = Math.min(newFilterData?.length, startIndex + pageSize);
    let currentDisplayRows = newFilterData?.slice(startIndex, endIndex);
    if (currentDisplayRows?.length === 0 && currentPage !== 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      startIndex = (newPage - 1) * pageSize;
      endIndex = Math.min(newFilterData?.length, startIndex + pageSize);
      currentDisplayRows = newFilterData?.slice(startIndex, endIndex);
    }
    setDisplayRows(currentDisplayRows);
    setDeleting(false);
    setChangingPrimary(false);
  }, [
    addModalStatus,
    changingPrimary,
    currentPage,
    deleting,
    fields,
    filterTerm,
    getFieldValue,
    pageSize
  ]);

  return (
    <form onSubmit={handleSubmit(data => console.log(data))}>
      <div className="PhoneNumberDataTable">
        <DataTable
          rows={displayRows || []}
          headers={headers}
          useZebraStyles={false}
          isSortable
          sortRow={() => {
            return 0;
          }}
          render={({
            rows,
            headers,
            getTableProps,
            getHeaderProps,
            getSelectionProps
          }) => (
            <TableContainer className="data_tableStyle">
              <TableToolbar>
                <TableToolbarContent>
                  <Search
                    className="PhoneManage__search"
                    labelText={t(messages.search)}
                    placeholder={t(messages.searchPlaceholder)}
                    value={filterTerm}
                    onChange={e => setFilterTerm(e.target.value)}
                  />
                  <Button
                    id="pmt__download"
                    renderIcon={Download}
                    iconDescription={t(messages.download)}
                    title={t(messages.download)}
                    onClick={handleExport}
                    hasIconOnly
                    kind="ghost"
                  />
                  <Button
                    id="pmt__upload"
                    renderIcon={Upload}
                    iconDescription={t(PhoneImportModalMessages.upload)}
                    title={t(PhoneImportModalMessages.upload)}
                    onClick={() => setOpenImportModal(true)}
                    hasIconOnly
                    kind="ghost"
                  />
                  <Button
                    id="pmt__delete"
                    renderIcon={TrashCan}
                    iconDescription={t(sharedMessages.delete)}
                    title={t(sharedMessages.delete)}
                    onClick={handleDeleteSelection}
                    disabled={selectedData.length === 0}
                    hasIconOnly
                    kind="ghost"
                  />
                </TableToolbarContent>
                <Button
                  id="pmt__add"
                  renderIcon={Add}
                  iconDescription={t(messages.addPhoneNumber)}
                  onClick={() => setAddModalStatus(MODAL_STATUS.OPEN)}
                  kind="primary">
                  <FormattedMessage {...messages.addPhoneNumber} />
                </Button>
              </TableToolbar>
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    <TableSelectAll
                      {...getSelectionProps()}
                      onSelect={() => toggleSelectAll()}
                      checked={
                        selectedData.length > 0 &&
                        selectedData.length === rows.length
                      }
                    />
                    {headers.map(header => (
                      <TableHeader
                        key={header.key}
                        isSortable={header.header && header.key !== 'action'}
                        {...getHeaderProps({
                          onClick: e => {
                            toggleSortOrder(e, header.key);
                          },
                          header
                        })}>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <div className="PhoneNumberDataTable__emptyState--container">
                      <img
                        className="PhoneNumberDataTable__emptyState-icon"
                        src={empty_phone_numbers_table}
                        alt={t(messages.emptyStateIcon)}
                      />
                      <div className="PhoneNumberDataTable__emptyState-heading">
                        {t(messages.emptyTableTitle)}
                      </div>
                      <div className="PhoneNumberDataTable__emptyState-description">
                        {t(messages.emptyTableDescription)}
                      </div>
                      <Button
                        renderIcon={Add}
                        iconDescription={t(messages.addPhoneNumber)}
                        onClick={() => setAddModalStatus(MODAL_STATUS.OPEN)}
                        kind="primary"
                        size="sm">
                        <FormattedMessage {...messages.addPhoneNumber} />
                      </Button>
                    </div>
                  ) : (
                    rows?.map(row => (
                      <TableRow key={row.id}>
                        <TableSelectRow
                          {...getSelectionProps({ row })}
                          onSelect={() => handleRowSelect(row)}
                          checked={selectedData.some(i => i === row.id)}
                        />
                        {row?.cells?.map(cell => {
                          if (cell.id === `${row.id}:primary`) {
                            const msg = cell.value ? 'PRIMARY' : '';
                            if (
                              watchedFields &&
                              watchedFields[row.id] &&
                              watchedFields[row.id].inUseError
                            ) {
                              return (
                                <TableCell key={cell.id}>
                                  {cell.value && (
                                    <span>
                                      {msg}
                                      <br />
                                    </span>
                                  )}
                                  <span className="PMImport--notice">
                                    {t(messages.rowNumInUse, {
                                      name: row.id
                                    })}
                                  </span>
                                </TableCell>
                              );
                            } else {
                              return <TableCell key={cell.id}>{msg}</TableCell>;
                            }
                          } else if (cell.id === `${row.id}:action`) {
                            return (
                              <TableCell key={cell.id}>
                                <div data-floating-menu-container>
                                  <OverflowMenu
                                    className="PhoneNumberDataTable__overflow"
                                    data-testid={`PhoneNumber__overflow-${cell.id}`}
                                    key={`PhoneNumber__overflow-${cell.id}`}
                                    flipped={true}
                                    aria-labelledby={`pmt_${row.id}:phone_number`}>
                                    <OverflowMenuItem
                                      requireTitle={false}
                                      itemText={t(messages.setPrimary)}
                                      onClick={() => handleSetPrimary(row.id)}
                                    />
                                    <OverflowMenuItem
                                      requireTitle={false}
                                      itemText={t(sharedMessages.delete)}
                                      isDelete
                                      onClick={() => {
                                        handleDeleteSingleRow(row.id);
                                      }}
                                    />
                                  </OverflowMenu>
                                </div>
                              </TableCell>
                            );
                          } else {
                            return (
                              <TableCell key={cell.id} id={`pmt_${cell.id}`}>
                                {cell.value}
                              </TableCell>
                            );
                          }
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <Pagination
                totalItems={watchedFields?.length || 0}
                pageSize={pageSize}
                page={currentPage}
                pageSizes={PAGE_SIZES}
                onChange={change => {
                  setCurrentPage(change?.page);
                  setPageSize(change?.pageSize);
                }}
              />
            </TableContainer>
          )}
        />
        <div className="TabContent__spacer32" />
      </div>
      {openImportModal && (
        <PhoneImportModal
          openImportModal={openImportModal}
          handleImportSave={handleImportSave}
          handleImportClose={() => setOpenImportModal(false)}
          setImporting={setImporting}
          importing={importing}
        />
      )}
      {addModalStatus === MODAL_STATUS.OPEN && (
        <PhoneAddModal
          addModalStatus={addModalStatus}
          newNumAppended={newNumAppended}
          setAddModalStatus={setAddModalStatus}
          newNumIndex={watchedFields?.length || 0}
          readOnly={readOnly}
        />
      )}
    </form>
  );
};
